import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, ListChecks, Sparkle, Footprints, Binoculars } from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import GuideListenButton, { guideToSections } from "@/components/GuideListenButton";

const ACCENTS = {
  science: { text: "#1E40AF", bg: "#EFF6FF", border: "#BFDBFE", chip: "#DBEAFE" },
  weather: { text: "#0369A1", bg: "#F0F9FF", border: "#BAE6FD", chip: "#E0F2FE" },
  plants: { text: "#166534", bg: "#F0FDF4", border: "#BBF7D0", chip: "#DCFCE7" },
  species: { text: "#166534", bg: "#F0FDF4", border: "#BBF7D0", chip: "#DCFCE7" },
};

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

/** Per-tab long-form AI articles (generated by scripts/generate_tab_articles.py). */
const ARTICLE_BY_KEY = {
  "weather:home": "weather-tab-today",
  "weather:live": "weather-tab-live",
  "weather:now": "weather-tab-now",
  "weather:journal": "weather-tab-journal",
  "weather:learn": "weather-tab-learn",
  "weather:map": "weather-tab-map",
  "weather:place": "weather-tab-place",
  "weather:365": "weather-tab-365",
  "weather:station": "weather-tab-station",
  "weather:schools": "weather-tab-schools",
  "weather:schools-journal": "weather-tab-schools",
  "science:home": "science-tab-home",
  "science:today": "science-tab-today",
};

function articleForKey(pageKey) {
  if (ARTICLE_BY_KEY[pageKey]) return ARTICLE_BY_KEY[pageKey];
  if (pageKey?.startsWith("science:pillar:")) return `science-tab-${pageKey.split(":")[2]}`;
  return null;
}

/**
 * AI-authored "About this page" guide. Fetches /api/content/page?key=...
 * Handles both content shapes:
 *  - page guides:   intro, highlights, tips, did_you_know
 *  - group/pillar:  intro, identification_tips, beginner_steps, did_you_know, conservation_note
 * Renders nothing while loading or when no guide exists yet.
 */
export default function PageGuide({ pageKey, testidPrefix = "page-guide" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    if (!pageKey) return;
    http.get("/content/page", { params: { key: pageKey } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [pageKey]);

  if (!data?.content) return null;
  const c = data.content;
  const accent = ACCENTS[(pageKey || "").split(":")[0]] || ACCENTS.species;
  const photo = data.photo;

  return (
    <section
      className="rounded-3xl border p-6 md:p-8"
      style={{ backgroundColor: accent.bg, borderColor: accent.border }}
      data-testid={`${testidPrefix}-guide`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase" style={{ color: accent.text }}>
            About this page
          </div>
          {ARTICLE_BY_KEY[pageKey] || pageKey?.startsWith("science:pillar:") ? (
            <Link to={`/read/articles/${articleForKey(pageKey)}`} data-testid={`${testidPrefix}-article-link`}
              className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#B85F2A] hover:text-[#a25526]">
              Deep-dive article →
            </Link>
          ) : null}
        </div>
        <GuideListenButton sections={guideToSections(c)} testid={`${testidPrefix}-listen`} color={accent.text} />
      </div>
      <div className={`mt-3 grid gap-6 ${photo ? "lg:grid-cols-[1.6fr_1fr]" : ""}`}>
        <div className="space-y-5">
          {c.intro && (
            <p className="text-base text-[#374151] leading-relaxed whitespace-pre-line" data-testid={`${testidPrefix}-intro`}>
              {c.intro}
            </p>
          )}

          {c.highlights?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 font-semibold text-sm" style={{ color: accent.text }}>
                <ListChecks size={17} weight="bold" /> What you can do here
              </div>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {c.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#374151]" data-testid={`${testidPrefix}-highlight-${i}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: accent.text }} />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {c.identification_tips?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 font-semibold text-sm" style={{ color: accent.text }}>
                <Binoculars size={17} weight="bold" /> Getting started
              </div>
              <ul className="mt-2 space-y-1.5">
                {c.identification_tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#374151]" data-testid={`${testidPrefix}-tip-${i}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: accent.text }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {c.tips?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 font-semibold text-sm" style={{ color: accent.text }}>
                <Lightbulb size={17} weight="bold" /> Tips
              </div>
              <ul className="mt-2 space-y-1.5">
                {c.tips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#374151]" data-testid={`${testidPrefix}-tip-${i}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: accent.text }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {c.beginner_steps?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 font-semibold text-sm" style={{ color: accent.text }}>
                <Footprints size={17} weight="bold" /> First steps
              </div>
              <ol className="mt-2 space-y-1.5">
                {c.beginner_steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#374151]" data-testid={`${testidPrefix}-step-${i}`}>
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                      style={{ backgroundColor: accent.chip, color: accent.text }}>{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {c.did_you_know?.length > 0 && (
            <div className="rounded-2xl border bg-white/70 p-4" style={{ borderColor: accent.border }}>
              <div className="flex items-center gap-2 font-semibold text-sm" style={{ color: accent.text }}>
                <Sparkle size={16} weight="fill" /> Did you know?
              </div>
              <ul className="mt-2 space-y-1.5">
                {c.did_you_know.map((f, i) => (
                  <li key={i} className="text-sm text-[#374151] leading-relaxed" data-testid={`${testidPrefix}-fact-${i}`}>• {f}</li>
                ))}
              </ul>
            </div>
          )}

          {c.conservation_note && (
            <p className="text-sm text-[#4B5563] leading-relaxed italic" data-testid={`${testidPrefix}-note`}>
              {c.conservation_note}
            </p>
          )}
        </div>

        {photo && (
          <figure className="rounded-2xl overflow-hidden border bg-white shadow-sm self-start" style={{ borderColor: accent.border }} data-testid={`${testidPrefix}-photo`}>
            <img src={abs(photo.url)} alt={photo.alt} className="w-full object-cover" loading="lazy" />
            <figcaption className="px-3 py-2 text-xs text-[#6B7280]">
              {photo.ai_generated ? (
                <span data-testid={`${testidPrefix}-photo-ai`}>NaturePulse AI illustration</span>
              ) : (
                <>
                    Photo by{" "}
                    <a href={photo.photographer_link} target="_blank" rel="noreferrer" className="underline underline-offset-2" style={{ color: accent.text }} data-testid={`${testidPrefix}-photo-photographer`}>
                      {photo.photographer_name}
                    </a>{" "}
                    on{" "}
                    <a href={photo.unsplash_link} target="_blank" rel="noreferrer" className="underline underline-offset-2" style={{ color: accent.text }} data-testid={`${testidPrefix}-photo-unsplash`}>
                      Unsplash
                    </a>
                </>
              )}
            </figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}
